/**
 * Adaptive rendering-quality tier (issue #17). The viewer runs fine on an
 * Apple M4 but stutters on integrated-GPU business laptops: full-scene soft
 * shadows, 2x pixel ratio on hi-DPI panels, and MSAA are the main GPU cost
 * drivers. Rather than degrade quality for everyone, we auto-detect a low tier
 * (or take a manual override) and drop those costs on weak hardware while
 * leaving the high-end experience untouched.
 *
 * Pure and dependency-free so the detection/settings logic is unit-tested in
 * Node; main.ts reads `navigator`/`location` and applies the result.
 */
export type QualityTier = 'low' | 'medium' | 'high';

export interface QualityEnv {
  /** navigator.hardwareConcurrency (logical cores), if known. */
  hardwareConcurrency: number | undefined;
  /** window.devicePixelRatio. */
  devicePixelRatio: number;
  /** Unmasked GPU renderer string (WEBGL_debug_renderer_info), if available. */
  gpuRenderer: string | undefined;
  /** Explicit override (URL / toggle) that wins over auto-detection. */
  override?: QualityTier;
}

export interface QualitySettings {
  /** Real-time sun shadows (shadow map pass + per-object cast/receive). */
  shadows: boolean;
  /** WebGL MSAA. */
  antialias: boolean;
  /** Upper bound applied to devicePixelRatio when sizing the drawing buffer. */
  pixelRatioCap: number;
  /** Sun shadow map resolution (only relevant when shadows are on). */
  shadowMapSize: number;
  /** Multiplier on roadside vegetation planting density (issue #44): draw
   * calls stay bounded either way (a fixed handful of InstancedMesh buckets),
   * but a weak GPU still pays per-instance vertex/shadow cost, so the low
   * tier plants fewer instances. */
  vegetationDensityScale: number;
  /** When true, roadside vegetation skips ground-cover instances entirely,
   * keeping only the billboard cross-plane tree/shrub cards (issue #44 low
   * quality tier: "billboarded-only"). */
  vegetationBillboardOnly: boolean;
  /** Global cap on simultaneously-live near-camera tree instances realised
   * inside OSM woodland polygons (issue #56). Draw calls stay bounded either
   * way (a fixed handful of InstancedMesh buckets), but a weak GPU still pays
   * per-instance vertex + shadow cost, so the low tier keeps far fewer trees
   * live at once. The distant flat-canopy overview is unaffected (near-zero
   * cost on both tiers). */
  treeInstanceCap: number;
  /** Global cap on trees drawn as the full baked branched MESH within the mesh
   * radius (issue #58). Each mesh tree is ~hundreds of triangles vs a card's
   * ~4, so this is kept small and only the closest trees pay it; the low tier
   * sets it to 0 (billboard-only). Independent of treeInstanceCap, which bounds
   * the mid-distance billboard band. */
  treeMeshInstanceCap: number;
  /** Whether the bloom post pass (issue #64) runs at all. A weak GPU skips it
   * entirely — the pass is disabled, not just driven to zero strength — so it
   * costs nothing on the low tier, matching the shadows/antialias pattern
   * above. */
  bloom: boolean;
  /** Global cap on procedural rock/boulder instances near the route and
   * viewpoints (issue #63). Already a small, bounded count on the high tier
   * (tens to low hundreds, per the issue's AC) — the low tier trims it
   * further so a weak GPU pays less per-instance vertex/shadow cost, the same
   * reasoning as treeInstanceCap. */
  rockInstanceCap: number;
}

/** GPU renderer substrings that indicate integrated or software rendering,
 * where the full-quality path is too expensive. */
const WEAK_GPU_PATTERN = /(intel|integrated|hd graphics|uhd|iris|llvmpipe|swiftshader|mesa|microsoft basic|software)/i;

/**
 * Decides the rendering tier. An explicit override always wins. Otherwise a
 * machine is 'low' when its GPU string looks integrated/software or it has few
 * logical cores; unknown hardware defaults to 'high' so we never silently
 * degrade a capable-but-unreported machine (e.g. an M4, which reports "Apple").
 */
export function detectQualityTier(env: QualityEnv): QualityTier {
  if (env.override) return env.override;
  if (env.gpuRenderer && WEAK_GPU_PATTERN.test(env.gpuRenderer)) return 'low';
  if (env.hardwareConcurrency !== undefined && env.hardwareConcurrency <= 4) return 'low';
  return 'high';
}

const HIGH: QualitySettings = {
  shadows: true,
  antialias: true,
  pixelRatioCap: 2,
  shadowMapSize: 2048,
  vegetationDensityScale: 1,
  vegetationBillboardOnly: false,
  treeInstanceCap: 6000,
  treeMeshInstanceCap: 400,
  bloom: true,
  rockInstanceCap: 220,
};
const LOW: QualitySettings = {
  shadows: false,
  antialias: false,
  pixelRatioCap: 1,
  shadowMapSize: 1024,
  vegetationDensityScale: 0.4,
  vegetationBillboardOnly: true,
  treeInstanceCap: 2000,
  treeMeshInstanceCap: 0,
  bloom: false,
  rockInstanceCap: 70,
};
/** Middle tier (issue #72): a manual-only choice, never auto-selected, for
 * machines that are neither confidently high-end nor confidently weak. Sits
 * between LOW and HIGH on every axis except shadow map size and antialias,
 * which are kept at HIGH-like settings since they are comparatively cheap
 * relative to instance-count-driven vegetation/rock costs. */
const MEDIUM: QualitySettings = {
  shadows: true,
  antialias: true,
  pixelRatioCap: 1.5,
  shadowMapSize: 1024,
  vegetationDensityScale: 0.7,
  vegetationBillboardOnly: false,
  treeInstanceCap: 3000,
  treeMeshInstanceCap: 100,
  bloom: false,
  rockInstanceCap: 140,
};

export function qualitySettings(tier: QualityTier): QualitySettings {
  if (tier === 'high') return HIGH;
  if (tier === 'medium') return MEDIUM;
  return LOW;
}

/** Reads `?quality=low|medium|high` from a location search string; ignores anything else. */
export function parseQualityOverride(search: string): QualityTier | undefined {
  const value = new URLSearchParams(search).get('quality');
  return value === 'low' || value === 'medium' || value === 'high' ? value : undefined;
}

/**
 * Storage-like interface for persisting a user's manual quality choice.
 * Deliberately narrow (get/set only) and injected rather than imported so this
 * module never touches `window.localStorage` directly and stays Node-testable.
 * Both methods may throw (e.g. `SecurityError` in private browsing with
 * storage disabled); callers of the helpers below must not need to catch —
 * the helpers absorb that themselves.
 */
export interface QualityStorage {
  get(key: string): string | null | undefined;
  set(key: string, value: string): void;
}

/** localStorage key used for the persisted manual quality choice. */
export const QUALITY_STORAGE_KEY = 'ray2.qualityTier';

function isQualityTier(value: unknown): value is QualityTier {
  return value === 'low' || value === 'medium' || value === 'high';
}

/**
 * Reads the saved quality tier, if any. Returns undefined (never throws) when
 * nothing is saved, the saved value doesn't parse (corrupted, or from some
 * future version we don't recognise), or storage access itself throws — all
 * three are treated identically as "no saved choice".
 */
export function loadSavedQualityTier(storage: QualityStorage): QualityTier | undefined {
  try {
    const raw = storage.get(QUALITY_STORAGE_KEY);
    return isQualityTier(raw) ? raw : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Persists a manually-chosen quality tier. Returns true on success, false if
 * storage access threw (e.g. private browsing) — the caller can surface that
 * to the user, but this never throws itself: a failed save silently no-ops.
 */
export function saveQualityTier(storage: QualityStorage, tier: QualityTier): boolean {
  try {
    storage.set(QUALITY_STORAGE_KEY, tier);
    return true;
  } catch {
    return false;
  }
}

/** Hardware/environment inputs for {@link resolveQualityTier}'s auto-detect
 * fallback. Same shape as {@link QualityEnv} minus `override` — the resolver
 * derives its own override from the URL, so a caller-supplied override here
 * would be redundant and is intentionally excluded from the type. */
export type QualityAutoDetectEnv = Omit<QualityEnv, 'override'>;

/**
 * Full precedence resolver (issue #72): URL `?quality=` beats a saved user
 * choice, which beats auto-detection. `medium` is reachable only via the URL
 * or a saved choice — {@link detectQualityTier}'s auto-detect heuristic is
 * unchanged and never returns it.
 *
 * Deliberately does not persist the URL override: a shared link carrying
 * `?quality=low` must only affect that session, not permanently downgrade
 * whoever opens it (the caller decides separately whether/when to call
 * {@link saveQualityTier}, e.g. in response to an explicit UI choice).
 */
export function resolveQualityTier(params: {
  search: string;
  storage: QualityStorage;
  env: QualityAutoDetectEnv;
}): QualityTier {
  const urlOverride = parseQualityOverride(params.search);
  if (urlOverride) return urlOverride;

  const saved = loadSavedQualityTier(params.storage);
  if (saved) return saved;

  return detectQualityTier(params.env);
}
